// The Store dialogs, extracted with their frames from the old application (ADR-08 §2.1 "copy, then cut logic"):
//   StorePurchaseModal   ← StoreAddProductAndPurchaseModal.tsx:1184-2025 (frame, header, single / list switcher,
//                          name autocomplete with "مسجل مسبقاً", warehouse, quantity, cost, sale price, cart) and
//                          PurchaseFundingSection.tsx:42-843 (cash from a box / supplier on account / from the
//                          person's trust; the excess goes through the shared excess dialog, D-073)
//   StoreSellModal       ← App.tsx:55810-56336 (card "بيع": product picker from the header, quantity, price, box or
//                          customer account, cash received / remaining on account, buyer name / phone / address) and
//                          App.tsx:56339-57060 (cart "قائمة بيع جديدة")
//   WarehouseManagerModal← WarehouseManagerModal.tsx (list, add / edit, delete → archive, stock transfer form)
//   StoreWriteoffModal, StoreProductEditModal, StoreCancelDialog, StoreOperationDetails — new dialogs on the same
//                          frame (D-114 write-off and product edit; D-091 cancel with reason; details from the ledger).
// Kept: the frames (`rounded-3xl`, header tile, close button), the field and button classes, the mode switcher.
// Cut: every handler (`handleProductTx`, `handleMultiProductSell`, `handleProcessReturn`, the SAPM / PFS save paths,
// the warehouse handlers) and their arithmetic (weighted cost, profit, remaining on account, stock moves, R-15 – R-21)
// → `onSubmit(values)`: the container previews on the server (the engine computes every figure) and shows them in the
// PIN step (ADR-08 §2.1 item 2). Supplier / customer accounts are chosen by id (D-084, D-088).
// Changed by decision: quantities are whole units (D-110); cost above the sale price only warns in both modes (D-111);
// cash + on-account must equal the total (D-112, the server refuses otherwise); an optional invoice discount (D-112,
// D-016 (c)); choosing another warehouse adds stock there and never moves existing stock (D-111, T-32); a warehouse is
// archived only when empty, the main one never (D-114); a product is written off with reason + PIN, archived at zero.
// Added: data-testid attributes. [C34] restored: the purchase modal's print toggle (old StoreAddProductAndPurchaseModal.tsx:1570-1585 /
// :1924-1939, shown because the old App passed `onPrintPurchaseReceipt`, App.tsx:55761-55808).
import { motion } from 'motion/react';
import { AlertTriangle, ArrowLeftRight, Archive, Building2, CheckCircle2, Edit3, Package, PackagePlus, Plus, Printer, Receipt, RotateCcw, ShoppingCart, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CustomSelect, type SelectOption } from '../components/CustomSelect.js';
import { NumericInput } from '../components/NumericInput.js';
import { useI18n } from '../i18n-context.js';

const inputCls = 'w-full bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-2xl px-4 py-3 text-right font-bold text-gray-800 dark:text-gray-100 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all';
const labelCls = 'block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5';
const units = (v: string) => v.replace(/\..*$/, '');

function Frame({ open, onClose, icon, iconCls, title, subtitle, wide, children, testid }: { open: boolean; onClose: () => void; icon: ReactNode; iconCls: string; title: string; subtitle?: string; wide?: boolean; children: ReactNode; testid: string }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid={testid}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 10 }}
        className={`bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-[#30363d] rounded-3xl w-full ${wide ? 'max-w-2xl lg:max-w-3xl' : 'max-w-lg'} shadow-2xl overflow-hidden my-auto max-h-[92vh] flex flex-col`}
      >
        <div className="p-4 sm:p-5 border-b border-gray-150 dark:border-[#30363d] bg-gray-50/80 dark:bg-[#161b22]/80 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-inner ${iconCls}`}>{icon}</div>
            <div>
              <h3 className="text-base sm:text-lg font-black text-gray-900 dark:text-white">{title}</h3>
              {subtitle && <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">{subtitle}</p>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/50 dark:hover:bg-[#21262d] rounded-xl transition-all cursor-pointer">
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">{children}</div>
      </motion.div>
    </div>
  );
}

// ------------------------------------------------------------------ purchase
export interface StoreProductOption {
  readonly id: string;
  readonly name: string;
  readonly salePrice: string;
  readonly costLabel: string;
  readonly stockLabel: string;
}

export interface StorePurchaseLine {
  readonly productId: string | null;
  readonly name: string;
  readonly warehouseId: string;
  readonly quantity: string;
  readonly unitCost: string;
  readonly salePrice: string;
}

export interface StoreFundingValues {
  readonly kind: 'cash' | 'supplier' | 'from_trust';
  readonly cashAccountId: string;
  readonly partyAccountId: string;
  /** a new supplier typed by name (opened inside the purchase, D-018) */
  readonly newPartyName: string;
  /** typed cash part (supplier: down payment; from trust: the shortage in cash); "" = none */
  readonly cash: string;
}

export interface StorePurchaseValues {
  readonly lines: readonly StorePurchaseLine[];
  readonly funding: StoreFundingValues;
  readonly note: string;
  /** [C34] old "طباعة وصل / سند الشراء بعد الحفظ" toggle (StoreAddProductAndPurchaseModal.tsx:1570-1585, :1924-1939) */
  readonly print: boolean;
}

export interface StorePurchaseModalProps {
  readonly open: boolean;
  readonly themeColor: string;
  readonly products: readonly StoreProductOption[];
  readonly warehouseOptions: readonly SelectOption[];
  readonly defaultWarehouseId: string;
  readonly initial?: { readonly productId: string; readonly warehouseId: string } | null;
  readonly cashAccountOptions: readonly SelectOption[];
  readonly defaultCashAccountId: string;
  readonly supplierOptions: readonly SelectOption[];
  readonly trustOptions: readonly SelectOption[];
  readonly busy: boolean;
  readonly onSubmit: (v: StorePurchaseValues) => void;
  readonly onClose: () => void;
}

const emptyLine = (warehouseId: string): StorePurchaseLine => ({ productId: null, name: '', warehouseId, quantity: '', unitCost: '', salePrice: '' });

export function StorePurchaseModal(p: StorePurchaseModalProps) {
  const { t } = useI18n();
  const [mode, setMode] = useState<'single' | 'list'>('single');
  const [line, setLine] = useState<StorePurchaseLine>(emptyLine(p.defaultWarehouseId));
  const [cart, setCart] = useState<StorePurchaseLine[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [funding, setFunding] = useState<StoreFundingValues>({ kind: 'cash', cashAccountId: p.defaultCashAccountId, partyAccountId: '', newPartyName: '', cash: '' });
  const [note, setNote] = useState('');
  const [shouldPrint, setShouldPrint] = useState(false);

  useEffect(() => {
    if (!p.open) return;
    const prod = p.initial ? p.products.find((x) => x.id === p.initial!.productId) : undefined;
    setMode('single');
    setCart([]);
    setNote('');
    setShouldPrint(false);
    setFunding({ kind: 'cash', cashAccountId: p.defaultCashAccountId, partyAccountId: '', newPartyName: '', cash: '' });
    setLine(prod ? { productId: prod.id, name: prod.name, warehouseId: p.initial!.warehouseId, quantity: '', unitCost: '', salePrice: prod.salePrice } : emptyLine(p.defaultWarehouseId));
  }, [p.open]);

  const matches = useMemo(() => {
    const q = line.name.trim().toLowerCase();
    return q ? p.products.filter((x) => x.name.toLowerCase().includes(q)).slice(0, 8) : p.products.slice(0, 8);
  }, [line.name, p.products]);
  const existing = p.products.find((x) => x.name.trim().toLowerCase() === line.name.trim().toLowerCase()) ?? null;
  const costAboveSale = line.unitCost !== '' && line.salePrice !== '' && Number(line.unitCost) > Number(line.salePrice);
  const lineOk = line.name.trim() !== '' && /^[1-9]\d*$/.test(line.quantity) && line.unitCost !== '' && Number(line.unitCost) > 0 && line.warehouseId !== '';
  const fundingOk = funding.kind === 'cash' ? funding.cashAccountId !== '' : funding.partyAccountId !== '' || funding.newPartyName.trim() !== '';
  const lines = mode === 'single' ? [line] : cart;
  const canSave = !p.busy && fundingOk && (mode === 'single' ? lineOk : cart.length > 0);

  const pick = (o: StoreProductOption) => {
    setLine((l) => ({ ...l, productId: o.id, name: o.name, salePrice: l.salePrice || o.salePrice }));
    setShowSuggest(false);
  };
  const finalLine = (l: StorePurchaseLine): StorePurchaseLine => ({ ...l, productId: existing && l === line ? existing.id : l.productId });

  return (
    <Frame open={p.open} onClose={p.onClose} icon={<PackagePlus size={22} />} iconCls="bg-blue-50 dark:bg-blue-900/20 text-blue-600" title={t('st.purchase_title')} subtitle={t('st.purchase_sub')} wide testid="store-purchase-modal">
      <div className="grid grid-cols-2 gap-2 bg-white dark:bg-[#0d1117] p-1.5 rounded-2xl border border-gray-200/90 dark:border-[#30363d] shadow-xs">
        <button type="button" onClick={() => setMode('single')} className={`py-2.5 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 cursor-pointer ${mode === 'single' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#161b22]'}`}>
          <Package size={16} />
          <span>{t('st.purchase_single')}</span>
        </button>
        <button type="button" data-testid="store-purchase-list-mode" onClick={() => setMode('list')} className={`py-2.5 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 cursor-pointer ${mode === 'list' ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#161b22]'}`}>
          <Receipt size={16} />
          <span>{t('st.purchase_list')}</span>
          {cart.length > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white text-emerald-700">{cart.length}</span>}
        </button>
      </div>

      <div className="space-y-4">
        <div className="text-right relative">
          <div className="flex justify-between items-center mb-1.5">
            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">{t('st.purchase_name')}</label>
            {existing && (
              <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-lg border border-emerald-200 dark:border-emerald-800/40">
                <CheckCircle2 size={13} />
                <span>{t('st.purchase_existing')}</span>
              </span>
            )}
          </div>
          <input
            type="text"
            data-testid="store-purchase-name"
            value={line.name}
            onChange={(e) => {
              setLine((l) => ({ ...l, name: e.target.value, productId: null }));
              setShowSuggest(true);
            }}
            onFocus={() => setShowSuggest(true)}
            className={inputCls}
            placeholder={t('st.purchase_name_placeholder')}
          />
          {showSuggest && matches.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-2xl shadow-xl max-h-56 overflow-y-auto">
              {matches.map((o) => (
                <button key={o.id} type="button" onMouseDown={() => pick(o)} className="w-full text-right px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center justify-between text-xs">
                  <span className="font-black text-gray-800 dark:text-gray-100">{o.name}</span>
                  <span className="font-mono text-gray-500">{o.stockLabel} · {o.costLabel}</span>
                </button>
              ))}
            </div>
          )}
          {existing && (
            <p className="mt-1.5 text-[11px] font-bold text-blue-600 dark:text-blue-400">{t('st.purchase_existing_hint', { stock: existing.stockLabel, cost: existing.costLabel })}</p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>{t('st.warehouse')}</label>
            <CustomSelect value={line.warehouseId} onChange={(v) => setLine((l) => ({ ...l, warehouseId: String(v) }))} options={[...p.warehouseOptions]} headerTitle={t('st.filter_wh_header')} searchPlaceholder={t('st.filter_wh_search')} size="md" />
          </div>
          <div>
            <label className={labelCls}>{t('st.quantity')}</label>
            <NumericInput value={line.quantity} onChange={(v) => setLine((l) => ({ ...l, quantity: units(v) }))} className={inputCls} placeholder="0" />
          </div>
          <div>
            <label className={labelCls}>{t('st.unit_cost')}</label>
            <NumericInput value={line.unitCost} onChange={(v) => setLine((l) => ({ ...l, unitCost: v }))} className={inputCls} placeholder="0" />
          </div>
          <div>
            <label className={labelCls}>{t('st.sale_price')}</label>
            <NumericInput value={line.salePrice} onChange={(v) => setLine((l) => ({ ...l, salePrice: v }))} className={inputCls} placeholder="0" />
          </div>
        </div>
        {costAboveSale && (
          <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-2">
            <AlertTriangle size={14} />
            <span>{t('st.cost_above_sale')}</span>
          </div>
        )}
        {mode === 'list' && (
          <div className="space-y-3">
            <button
              type="button"
              data-testid="store-purchase-add-line"
              disabled={!lineOk}
              onClick={() => {
                setCart((c) => [...c, finalLine(line)]);
                setLine(emptyLine(line.warehouseId));
              }}
              className="w-full py-2.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Plus size={16} />
              <span>{t('st.purchase_add_line')}</span>
            </button>
            {cart.length > 0 && (
              <div className="rounded-2xl border border-gray-100 dark:border-[#30363d] divide-y divide-gray-100 dark:divide-gray-800">
                {cart.map((c, i) => (
                  <div key={`pc-${i}`} className="flex items-center justify-between px-4 py-2.5 text-xs">
                    <span className="font-black text-gray-800 dark:text-gray-100">{c.name}</span>
                    <span className="font-mono text-gray-500">
                      {c.quantity} × {c.unitCost}
                    </span>
                    <button type="button" onClick={() => setCart((x) => x.filter((_, j) => j !== i))} className="p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <FundingSection value={funding} onChange={setFunding} cashAccountOptions={p.cashAccountOptions} supplierOptions={p.supplierOptions} trustOptions={p.trustOptions} />

      <div>
        <label className={labelCls}>{t('st.note')}</label>
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
      </div>
      {/* Print Receipt Toggle (old :1570-1585 single, :1924-1939 list) */}
      <div className="flex items-center gap-2 pt-1">
        <input
          type="checkbox"
          id={mode === 'single' ? 'print-single-check' : 'print-list-check'}
          data-testid="store-purchase-print"
          checked={shouldPrint}
          onChange={(e) => setShouldPrint(e.target.checked)}
          className={`w-4 h-4 rounded cursor-pointer ${mode === 'single' ? 'text-blue-600 focus:ring-blue-500' : 'text-emerald-600 focus:ring-emerald-500'}`}
        />
        <label htmlFor={mode === 'single' ? 'print-single-check' : 'print-list-check'} className="text-xs font-bold text-gray-700 dark:text-gray-300 cursor-pointer flex items-center gap-1.5">
          <Printer size={14} className="text-gray-500" />
          <span>{mode === 'single' ? t('st.purchase_print_single') : t('st.purchase_print_list')}</span>
        </label>
      </div>
      <button
        type="button"
        data-testid="store-purchase-save"
        disabled={!canSave}
        onClick={() => p.onSubmit({ lines: (mode === 'single' ? [finalLine(line)] : lines).map((l) => ({ ...l })), funding, note, print: shouldPrint })}
        style={{ backgroundColor: p.themeColor }}
        className="w-full py-3.5 rounded-2xl text-white text-sm font-black flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
      >
        <CheckCircle2 size={18} />
        <span>{p.busy ? t('st.processing') : t('st.purchase_save')}</span>
      </button>
    </Frame>
  );
}

function FundingSection({ value, onChange, cashAccountOptions, supplierOptions, trustOptions }: { value: StoreFundingValues; onChange: (v: StoreFundingValues) => void; cashAccountOptions: readonly SelectOption[]; supplierOptions: readonly SelectOption[]; trustOptions: readonly SelectOption[] }) {
  const { t } = useI18n();
  const set = (patch: Partial<StoreFundingValues>) => onChange({ ...value, ...patch });
  const kindBtn = (k: StoreFundingValues['kind'], label: string) => (
    <button type="button" data-testid={`store-funding-${k}`} onClick={() => set({ kind: k, partyAccountId: '', newPartyName: '', cash: '' })} className={`flex-1 py-2 rounded-xl text-[11px] font-black transition-all ${value.kind === k ? 'bg-white dark:bg-[#21262d] text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'}`}>
      {label}
    </button>
  );
  return (
    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d] space-y-3">
      <div className="text-xs font-black text-slate-700 dark:text-slate-300">{t('st.funding_title')}</div>
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#0d1117] p-1 rounded-2xl border border-gray-200 dark:border-[#30363d]">
        {kindBtn('cash', t('st.funding_cash'))}
        {kindBtn('supplier', t('st.funding_supplier'))}
        {kindBtn('from_trust', t('st.funding_trust'))}
      </div>
      {value.kind !== 'cash' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>{value.kind === 'supplier' ? t('st.funding_supplier_account') : t('st.funding_trust_account')}</label>
            <CustomSelect value={value.partyAccountId} onChange={(v) => set({ partyAccountId: String(v), newPartyName: '' })} options={[...(value.kind === 'supplier' ? supplierOptions : trustOptions)]} placeholder={t('st.refund_customer_placeholder')} headerTitle={t('st.refund_customer_header')} searchPlaceholder={t('st.refund_customer_search')} size="md" />
          </div>
          {value.kind === 'supplier' && (
            <div>
              <label className={labelCls}>{t('st.funding_new_supplier')}</label>
              <input type="text" value={value.newPartyName} onChange={(e) => set({ newPartyName: e.target.value, partyAccountId: '' })} className={inputCls} placeholder={t('st.funding_new_supplier_placeholder')} />
            </div>
          )}
          <div>
            <label className={labelCls}>{value.kind === 'supplier' ? t('st.funding_down_payment') : t('st.funding_cash_part')}</label>
            <NumericInput value={value.cash} onChange={(v) => set({ cash: v })} className={inputCls} placeholder="0" />
          </div>
        </div>
      )}
      {(value.kind === 'cash' || value.cash !== '') && (
        <div>
          <label className={labelCls}>{t('st.funding_box')}</label>
          <CustomSelect value={value.cashAccountId} onChange={(v) => set({ cashAccountId: String(v) })} options={[...cashAccountOptions]} placeholder={t('st.refund_box_placeholder')} headerTitle={t('st.refund_box_header')} searchPlaceholder={t('st.refund_box_search')} size="md" />
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ sale (card and list)
export interface StoreSellLine {
  readonly productId: string;
  readonly warehouseId: string;
  readonly quantity: string;
  readonly unitPrice: string;
}

export interface StoreSellValues {
  readonly lines: readonly StoreSellLine[];
  readonly discount: string;
  readonly counterParty: 'box' | 'customer';
  readonly cashAccountId: string;
  readonly allocation: 'debt' | 'trust';
  readonly customerAccountId: string;
  readonly newCustomerName: string;
  readonly receivedCash: string;
  readonly remainingOnAccount: string;
  readonly buyerName: string;
  readonly buyerPhone: string;
  readonly buyerAddress: string;
  readonly note: string;
  readonly print: boolean;
}

export interface StoreStockOption {
  /** `${productId}|${warehouseId}` */
  readonly id: string;
  readonly productId: string;
  readonly warehouseId: string;
  readonly label: string;
  readonly subLabel: string;
  readonly salePrice: string;
}

export interface StoreSellModalProps {
  readonly open: boolean;
  readonly multi: boolean;
  readonly themeColor: string;
  readonly stockOptions: readonly StoreStockOption[];
  readonly initialStockId?: string | null;
  readonly cashAccountOptions: readonly SelectOption[];
  readonly defaultCashAccountId: string;
  readonly debtOptions: readonly SelectOption[];
  readonly trustOptions: readonly SelectOption[];
  readonly busy: boolean;
  readonly onSubmit: (v: StoreSellValues) => void;
  readonly onClose: () => void;
}

export function StoreSellModal(p: StoreSellModalProps) {
  const { t } = useI18n();
  const blank = { stockId: p.initialStockId ?? '', quantity: '', price: '' };
  const [cur, setCur] = useState(blank);
  const [cart, setCart] = useState<Array<{ stockId: string; quantity: string; price: string }>>([]);
  const [discount, setDiscount] = useState('');
  const [counterParty, setCounterParty] = useState<'box' | 'customer'>('box');
  const [cashAccountId, setCashAccountId] = useState(p.defaultCashAccountId);
  const [allocation, setAllocation] = useState<'debt' | 'trust'>('debt');
  const [customerAccountId, setCustomerAccountId] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [receivedCash, setReceivedCash] = useState('');
  const [remaining, setRemaining] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [buyerAddress, setBuyerAddress] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!p.open) return;
    const opt = p.stockOptions.find((o) => o.id === p.initialStockId);
    setCur({ stockId: p.initialStockId ?? '', quantity: '', price: opt?.salePrice ?? '' });
    setCart([]);
    setDiscount('');
    setCounterParty('box');
    setCashAccountId(p.defaultCashAccountId);
    setAllocation('debt');
    setCustomerAccountId('');
    setNewCustomerName('');
    setReceivedCash('');
    setRemaining('');
    setBuyerName('');
    setBuyerPhone('');
    setBuyerAddress('');
    setNote('');
  }, [p.open]);

  const selected = p.stockOptions.find((o) => o.id === cur.stockId);
  const curOk = !!selected && /^[1-9]\d*$/.test(cur.quantity) && cur.price !== '' && Number(cur.price) > 0;
  const rows = p.multi ? cart : curOk ? [cur] : [];
  const customerOk = counterParty === 'box' ? cashAccountId !== '' : customerAccountId !== '' || newCustomerName.trim() !== '';
  const canSave = !p.busy && rows.length > 0 && customerOk;

  const submit = (print: boolean) =>
    p.onSubmit({
      lines: rows.map((r) => {
        const o = p.stockOptions.find((x) => x.id === r.stockId)!;
        return { productId: o.productId, warehouseId: o.warehouseId, quantity: r.quantity, unitPrice: r.price };
      }),
      discount,
      counterParty,
      cashAccountId,
      allocation,
      customerAccountId,
      newCustomerName,
      receivedCash,
      remainingOnAccount: remaining,
      buyerName,
      buyerPhone,
      buyerAddress,
      note,
      print,
    });

  const title = p.multi ? t('st.new_sell_list') : selected ? t('st.sell_title', { name: selected.label }) : t('st.sell_product');

  return (
    <Frame open={p.open} onClose={p.onClose} icon={p.multi ? <Receipt size={20} /> : <ShoppingCart size={20} />} iconCls="bg-blue-100 dark:bg-blue-950/60 text-blue-600" title={title} {...(selected && !p.multi ? { subtitle: `${t('st.current_stock')} ${selected.subLabel}` } : {})} wide={p.multi} testid="store-sell-modal">
      <div className="text-right p-3 bg-slate-50 dark:bg-[#161b22] rounded-2xl border border-gray-100 dark:border-[#30363d] space-y-3">
        <div>
          <label className="block text-xs font-black text-slate-700 dark:text-slate-300 mb-1.5">{t('st.sell_pick_product')}</label>
          <CustomSelect
            value={cur.stockId}
            onChange={(v) => {
              const o = p.stockOptions.find((x) => x.id === String(v));
              setCur((c) => ({ ...c, stockId: String(v), price: o?.salePrice ?? c.price }));
            }}
            options={p.stockOptions.map((o) => ({ id: o.id, label: o.label, subLabel: o.subLabel }))}
            placeholder={t('st.sell_pick_placeholder')}
            headerTitle={t('st.product_filter_header')}
            searchPlaceholder={t('st.product_filter_search')}
            size="md"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>{t('st.quantity')}</label>
            <NumericInput value={cur.quantity} onChange={(v) => setCur((c) => ({ ...c, quantity: units(v) }))} className={inputCls} placeholder="0" />
          </div>
          <div>
            <label className={labelCls}>{t('st.unit_price')}</label>
            <NumericInput value={cur.price} onChange={(v) => setCur((c) => ({ ...c, price: v }))} className={inputCls} placeholder="0" />
          </div>
        </div>
        {p.multi && (
          <button
            type="button"
            data-testid="store-sell-add-line"
            disabled={!curOk}
            onClick={() => {
              setCart((c) => [...c, cur]);
              setCur({ stockId: '', quantity: '', price: '' });
            }}
            className="w-full py-2.5 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-black flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Plus size={16} />
            <span>{t('st.sell_add_line')}</span>
          </button>
        )}
      </div>

      {p.multi && cart.length > 0 && (
        <div className="rounded-2xl border border-gray-100 dark:border-[#30363d] divide-y divide-gray-100 dark:divide-gray-800">
          {cart.map((c, i) => {
            const o = p.stockOptions.find((x) => x.id === c.stockId);
            return (
              <div key={`sc-${i}`} className="flex items-center justify-between px-4 py-2.5 text-xs">
                <span className="font-black text-gray-800 dark:text-gray-100">{o?.label}</span>
                <span className="font-mono text-gray-500">
                  {c.quantity} × {c.price}
                </span>
                <button type="button" onClick={() => setCart((x) => x.filter((_, j) => j !== i))} className="p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg">
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div>
        <label className={labelCls}>{t('st.discount')}</label>
        <NumericInput value={discount} onChange={setDiscount} className={inputCls} placeholder="0" />
      </div>

      <div className="p-4 rounded-2xl bg-slate-50 dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d] space-y-3">
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#0d1117] p-1 rounded-2xl border border-gray-200 dark:border-[#30363d]">
          <button type="button" onClick={() => setCounterParty('box')} className={`flex-1 py-2 rounded-xl text-[11px] font-black ${counterParty === 'box' ? 'bg-white dark:bg-[#21262d] text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-500'}`}>
            {t('st.sell_to_box')}
          </button>
          <button type="button" data-testid="store-sell-customer" onClick={() => setCounterParty('customer')} className={`flex-1 py-2 rounded-xl text-[11px] font-black ${counterParty === 'customer' ? 'bg-white dark:bg-[#21262d] text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-500'}`}>
            {t('st.sell_to_customer')}
          </button>
        </div>
        {counterParty === 'customer' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t('st.allocation')}</label>
              <CustomSelect value={allocation} onChange={(v) => { setAllocation(v as 'debt' | 'trust'); setCustomerAccountId(''); }} options={[{ id: 'debt', label: t('st.pay_debt_badge') }, { id: 'trust', label: t('st.pay_trust_badge') }]} size="md" />
            </div>
            <div>
              <label className={labelCls}>{t('st.refund_customer')}</label>
              <CustomSelect value={customerAccountId} onChange={(v) => { setCustomerAccountId(String(v)); setNewCustomerName(''); }} options={[...(allocation === 'debt' ? p.debtOptions : p.trustOptions)]} placeholder={t('st.refund_customer_placeholder')} headerTitle={t('st.refund_customer_header')} searchPlaceholder={t('st.refund_customer_search')} size="md" />
            </div>
            {allocation === 'debt' && (
              <div>
                <label className={labelCls}>{t('st.new_customer')}</label>
                <input type="text" value={newCustomerName} onChange={(e) => { setNewCustomerName(e.target.value); setCustomerAccountId(''); }} className={inputCls} />
              </div>
            )}
            <div>
              <label className={labelCls}>{t('st.received_cash')}</label>
              <NumericInput value={receivedCash} onChange={(v) => { setReceivedCash(v); setRemaining(''); }} className={inputCls} placeholder="0" />
            </div>
            <div>
              <label className={labelCls}>{t('st.remaining_on_account')}</label>
              <NumericInput value={remaining} onChange={(v) => { setRemaining(v); setReceivedCash(''); }} className={inputCls} placeholder={t('st.remaining_auto')} />
            </div>
          </div>
        )}
        {(counterParty === 'box' || receivedCash !== '' || remaining !== '') && (
          <div>
            <label className={labelCls}>{t('st.funding_box')}</label>
            <CustomSelect value={cashAccountId} onChange={(v) => setCashAccountId(String(v))} options={[...p.cashAccountOptions]} placeholder={t('st.refund_box_placeholder')} headerTitle={t('st.refund_box_header')} searchPlaceholder={t('st.refund_box_search')} size="md" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>{t('st.buyer_name')}</label>
          <input type="text" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t('st.buyer_phone')}</label>
          <input type="text" value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} className={inputCls} dir="ltr" />
        </div>
        <div>
          <label className={labelCls}>{t('st.buyer_address')}</label>
          <input type="text" value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls}>{t('st.note')}</label>
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
      </div>
      <div className="flex gap-3">
        <button type="button" data-testid="store-sell-save" disabled={!canSave} onClick={() => submit(false)} className="flex-1 py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-black flex items-center justify-center gap-2 shadow-lg disabled:opacity-50">
          <CheckCircle2 size={18} />
          <span>{p.busy ? t('st.processing') : t('st.save')}</span>
        </button>
        <button type="button" data-testid="store-sell-save-print" disabled={!canSave} onClick={() => submit(true)} style={{ backgroundColor: p.themeColor }} className="flex-1 py-3.5 rounded-2xl text-white text-sm font-black flex items-center justify-center gap-2 shadow-lg disabled:opacity-50">
          <Receipt size={18} />
          <span>{t('st.save_print')}</span>
        </button>
      </div>
    </Frame>
  );
}

// ------------------------------------------------------------------ warehouses
export interface StoreWarehouseRow {
  readonly id: string;
  readonly name: string;
  readonly location: string | null;
  readonly description: string | null;
  readonly isMain: boolean;
  readonly archived: boolean;
  readonly products: number;
  readonly unitsLabel: string;
}

export interface WarehouseManagerModalProps {
  readonly open: boolean;
  readonly warehouses: readonly StoreWarehouseRow[];
  readonly stockOptions: readonly StoreStockOption[];
  readonly busy: boolean;
  readonly onCreate: (v: { name: string; location: string; description: string }) => void;
  readonly onUpdate: (v: { id: string; name: string; location: string; description: string }) => void;
  readonly onArchive: (id: string, archive: boolean) => void;
  readonly onTransfer: (v: { productId: string; fromWarehouseId: string; toWarehouseId: string; quantity: string; note: string }) => void;
  readonly onClose: () => void;
}

export function WarehouseManagerModal(p: WarehouseManagerModalProps) {
  const { t } = useI18n();
  const [tab, setTab] = useState<'list' | 'transfer'>('list');
  const [edit, setEdit] = useState<{ id: string | null; name: string; location: string; description: string }>({ id: null, name: '', location: '', description: '' });
  const [tr, setTr] = useState({ stockId: '', to: '', quantity: '', note: '' });
  useEffect(() => {
    if (!p.open) return;
    setTab('list');
    setEdit({ id: null, name: '', location: '', description: '' });
    setTr({ stockId: '', to: '', quantity: '', note: '' });
  }, [p.open]);
  const src = p.stockOptions.find((o) => o.id === tr.stockId);
  const active = p.warehouses.filter((w) => !w.archived);
  return (
    <Frame open={p.open} onClose={p.onClose} icon={<Building2 size={22} />} iconCls="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600" title={t('st.manage_warehouses')} subtitle={t('st.wh_sub')} wide testid="store-warehouse-modal">
      <div className="grid grid-cols-2 gap-2 bg-white dark:bg-[#0d1117] p-1.5 rounded-2xl border border-gray-200/90 dark:border-[#30363d]">
        <button type="button" onClick={() => setTab('list')} className={`py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-2 ${tab === 'list' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 dark:text-gray-400'}`}>
          <Building2 size={16} />
          <span>{t('st.wh_tab_list')}</span>
        </button>
        <button type="button" data-testid="store-wh-tab-transfer" onClick={() => setTab('transfer')} className={`py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-2 ${tab === 'transfer' ? 'bg-amber-500 text-white shadow-md' : 'text-gray-600 dark:text-gray-400'}`}>
          <ArrowLeftRight size={16} />
          <span>{t('st.wh_tab_transfer')}</span>
        </button>
      </div>
      {tab === 'list' && (
        <div className="space-y-4">
          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d] space-y-3">
            <div className="text-xs font-black text-slate-700 dark:text-slate-300">{edit.id ? t('st.wh_edit') : t('st.wh_add')}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input type="text" data-testid="store-wh-name" value={edit.name} onChange={(e) => setEdit((x) => ({ ...x, name: e.target.value }))} placeholder={t('st.wh_name')} className={inputCls} />
              <input type="text" value={edit.location} onChange={(e) => setEdit((x) => ({ ...x, location: e.target.value }))} placeholder={t('st.wh_location')} className={inputCls} />
              <input type="text" value={edit.description} onChange={(e) => setEdit((x) => ({ ...x, description: e.target.value }))} placeholder={t('st.wh_description')} className={inputCls} />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="store-wh-save"
                disabled={p.busy || !edit.name.trim()}
                onClick={() => (edit.id ? p.onUpdate({ id: edit.id, name: edit.name, location: edit.location, description: edit.description }) : p.onCreate({ name: edit.name, location: edit.location, description: edit.description }))}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black disabled:opacity-50"
              >
                {edit.id ? t('st.save') : t('st.wh_add_btn')}
              </button>
              {edit.id && (
                <button type="button" onClick={() => setEdit({ id: null, name: '', location: '', description: '' })} className="px-5 py-2.5 rounded-xl bg-gray-100 dark:bg-[#21262d] text-gray-700 dark:text-gray-300 text-xs font-black">
                  {t('st.cancel')}
                </button>
              )}
            </div>
          </div>
          <div className="rounded-2xl border border-gray-100 dark:border-[#30363d] divide-y divide-gray-100 dark:divide-gray-800">
            {p.warehouses.map((w) => (
              <div key={w.id} className={`flex items-center justify-between px-4 py-3 text-xs ${w.archived ? 'opacity-50' : ''}`} data-testid={`store-wh-row-${w.id}`}>
                <div>
                  <div className="font-black text-gray-900 dark:text-white flex items-center gap-2">
                    🏢 {w.name}
                    {w.isMain && <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-600">{t('st.wh_main')}</span>}
                    {w.archived && <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500">{t('st.archived')}</span>}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{[w.location, w.description].filter(Boolean).join(' · ') || '-'}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-gray-500">{t('st.wh_counts', { products: w.products, units: w.unitsLabel })}</span>
                  {!w.archived && (
                    <button type="button" onClick={() => setEdit({ id: w.id, name: w.name, location: w.location ?? '', description: w.description ?? '' })} className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg" title={t('st.wh_edit')}>
                      <Edit3 size={14} />
                    </button>
                  )}
                  {!w.isMain && (
                    <button type="button" data-testid={`store-wh-archive-${w.id}`} onClick={() => p.onArchive(w.id, !w.archived)} className={`p-1.5 rounded-lg ${w.archived ? 'text-emerald-600 hover:bg-emerald-50' : 'text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30'}`} title={w.archived ? t('st.restore') : t('st.archive')}>
                      {w.archived ? <RotateCcw size={14} /> : <Archive size={14} />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === 'transfer' && (
        <div className="space-y-4">
          <div>
            <label className={labelCls}>{t('st.tr_source')}</label>
            <CustomSelect value={tr.stockId} onChange={(v) => setTr((x) => ({ ...x, stockId: String(v) }))} options={p.stockOptions.map((o) => ({ id: o.id, label: o.label, subLabel: o.subLabel }))} placeholder={t('st.sell_pick_placeholder')} headerTitle={t('st.product_filter_header')} searchPlaceholder={t('st.product_filter_search')} size="md" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t('st.tr_target')}</label>
              <CustomSelect value={tr.to} onChange={(v) => setTr((x) => ({ ...x, to: String(v) }))} options={active.filter((w) => w.id !== src?.warehouseId).map((w) => ({ id: w.id, label: `🏢 ${w.name}` }))} headerTitle={t('st.filter_wh_header')} searchPlaceholder={t('st.filter_wh_search')} size="md" />
            </div>
            <div>
              <label className={labelCls}>{t('st.quantity')}</label>
              <NumericInput value={tr.quantity} onChange={(v) => setTr((x) => ({ ...x, quantity: units(v) }))} className={inputCls} placeholder="0" />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t('st.note')}</label>
            <input type="text" value={tr.note} onChange={(e) => setTr((x) => ({ ...x, note: e.target.value }))} className={inputCls} />
          </div>
          <button
            type="button"
            data-testid="store-tr-save"
            disabled={p.busy || !src || !tr.to || !/^[1-9]\d*$/.test(tr.quantity)}
            onClick={() => src && p.onTransfer({ productId: src.productId, fromWarehouseId: src.warehouseId, toWarehouseId: tr.to, quantity: tr.quantity, note: tr.note })}
            className="w-full py-3.5 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-black flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <ArrowLeftRight size={18} />
            <span>{p.busy ? t('st.processing') : t('st.tr_execute')}</span>
          </button>
        </div>
      )}
    </Frame>
  );
}

// ------------------------------------------------------------------ write-off / archive, edit, cancel, details
export interface StoreWriteoffModalProps {
  readonly open: boolean;
  readonly productName: string;
  readonly warehouseName: string;
  readonly stockLabel: string;
  readonly hasStock: boolean;
  readonly canWriteoff: boolean;
  readonly canArchive: boolean;
  readonly busy: boolean;
  readonly onWriteoff: (v: { quantity: string; reason: string }) => void;
  readonly onArchive: () => void;
  readonly onClose: () => void;
}

export function StoreWriteoffModal(p: StoreWriteoffModalProps) {
  const { t } = useI18n();
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (p.open) {
      setQuantity('');
      setReason('');
    }
  }, [p.open]);
  return (
    <Frame open={p.open} onClose={p.onClose} icon={<Trash2 size={20} />} iconCls="bg-rose-100 dark:bg-rose-950/60 text-rose-600" title={t('st.remove_title')} subtitle={`${p.productName} — ${p.warehouseName}`} testid="store-writeoff-modal">
      <p className="text-xs font-bold text-gray-600 dark:text-gray-300">{t('st.writeoff_hint', { stock: p.stockLabel })}</p>
      {p.hasStock && p.canWriteoff && (
        <div className="space-y-3">
          <div>
            <label className={labelCls}>{t('st.quantity')}</label>
            <NumericInput value={quantity} onChange={(v) => setQuantity(units(v))} className={inputCls} placeholder="0" />
          </div>
          <div>
            <label className={labelCls}>{t('st.writeoff_reason')}</label>
            <input type="text" data-testid="store-writeoff-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
          </div>
          <button type="button" data-testid="store-writeoff-save" disabled={p.busy || !/^[1-9]\d*$/.test(quantity) || !reason.trim()} onClick={() => p.onWriteoff({ quantity, reason: reason.trim() })} className="w-full py-3 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-black disabled:opacity-50">
            {p.busy ? t('st.processing') : t('st.writeoff_execute')}
          </button>
        </div>
      )}
      {p.canArchive && (
        <button type="button" data-testid="store-product-archive" disabled={p.busy || p.hasStock} onClick={p.onArchive} className="w-full py-3 rounded-2xl bg-gray-100 dark:bg-[#21262d] text-gray-800 dark:text-gray-200 text-sm font-black flex items-center justify-center gap-2 disabled:opacity-50">
          <Archive size={16} />
          <span>{p.hasStock ? t('st.archive_needs_zero') : t('st.archive_product')}</span>
        </button>
      )}
    </Frame>
  );
}

export function StoreProductEditModal({ open, name, salePrice, busy, onSave, onClose }: { open: boolean; name: string; salePrice: string; busy: boolean; onSave: (v: { name: string; salePrice: string }) => void; onClose: () => void }) {
  const { t } = useI18n();
  const [n, setN] = useState(name);
  const [s, setS] = useState(salePrice);
  useEffect(() => {
    if (open) {
      setN(name);
      setS(salePrice);
    }
  }, [open, name, salePrice]);
  return (
    <Frame open={open} onClose={onClose} icon={<Edit3 size={20} />} iconCls="bg-amber-100 dark:bg-amber-950/60 text-amber-600" title={t('st.edit_product')} subtitle={t('st.edit_product_hint')} testid="store-edit-modal">
      <div>
        <label className={labelCls}>{t('st.purchase_name')}</label>
        <input type="text" data-testid="store-edit-name" value={n} onChange={(e) => setN(e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>{t('st.sale_price')}</label>
        <NumericInput value={s} onChange={setS} className={inputCls} />
      </div>
      <button type="button" data-testid="store-edit-save" disabled={busy || !n.trim() || s === ''} onClick={() => onSave({ name: n.trim(), salePrice: s })} className="w-full py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-black disabled:opacity-50">
        {busy ? t('st.processing') : t('st.save')}
      </button>
    </Frame>
  );
}

export function StoreCancelDialog({ open, title, busy, onConfirm, onClose }: { open: boolean; title: string; busy: boolean; onConfirm: (reason: string) => void; onClose: () => void }) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  return (
    <Frame open={open} onClose={onClose} icon={<AlertTriangle size={20} />} iconCls="bg-rose-100 dark:bg-rose-950/60 text-rose-600" title={title} subtitle={t('st.cancel_hint')} testid="store-cancel-dialog">
      <div>
        <label className={labelCls}>{t('st.cancel_reason')}</label>
        <textarea data-testid="store-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className={inputCls} />
      </div>
      <button type="button" data-testid="store-cancel-confirm" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())} className="w-full py-3 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-black disabled:opacity-50">
        {busy ? t('st.processing') : t('st.cancel_confirm')}
      </button>
    </Frame>
  );
}

export interface StoreOperationDetailsView {
  readonly title: string;
  readonly fields: ReadonlyArray<{ readonly label: string; readonly value: string }>;
  readonly lines: ReadonlyArray<{ readonly name: string; readonly warehouse: string; readonly qty: string; readonly price: string; readonly total: string }>;
}

export function StoreOperationDetails({ view, onClose, onPrint }: { view: StoreOperationDetailsView | null; onClose: () => void; onPrint?: () => void }) {
  const { t } = useI18n();
  return (
    <Frame open={view !== null} onClose={onClose} icon={<Package size={20} />} iconCls="bg-blue-100 dark:bg-blue-950/60 text-blue-600" title={view?.title ?? ''} wide testid="store-details-modal">
      {view && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {view.fields.map((f) => (
              <div key={f.label} className="p-3 rounded-xl bg-gray-50 dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d] flex items-center justify-between text-xs">
                <span className="font-bold text-gray-500">{f.label}</span>
                <span className="font-black text-gray-900 dark:text-white font-mono">{f.value}</span>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-[#30363d]">
            <table className="w-full text-right text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-[#161b22] text-gray-600 dark:text-gray-400 font-black">
                  <th className="py-2.5 px-3">{t('st.ret_col_product')}</th>
                  <th className="py-2.5 px-3">{t('st.warehouse')}</th>
                  <th className="py-2.5 px-3 text-center">{t('st.quantity')}</th>
                  <th className="py-2.5 px-3 text-center">{t('st.unit_price')}</th>
                  <th className="py-2.5 px-3 text-center">{t('st.col_total')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {view.lines.map((l, i) => (
                  <tr key={`dl-${i}`}>
                    <td className="py-2.5 px-3 font-bold">{l.name}</td>
                    <td className="py-2.5 px-3">{l.warehouse}</td>
                    <td className="py-2.5 px-3 text-center font-mono">{l.qty}</td>
                    <td className="py-2.5 px-3 text-center font-mono">{l.price}</td>
                    <td className="py-2.5 px-3 text-center font-mono">{l.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {onPrint && (
            <button type="button" onClick={onPrint} className="w-full py-3 rounded-2xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-black">
              {t('st.print_invoice')}
            </button>
          )}
        </>
      )}
    </Frame>
  );
}
